import React, { useEffect, useState } from "react";
import { X, Mail, Phone, Building2, User, MessageSquare, Trash2, Send, RefreshCcw } from "lucide-react";
import { patchSubmission, deleteSubmission, sendRecoveryEmail, resendConfirmation } from "@/lib/adminApi";
import { toast } from "sonner";
import { Chip, FORM_LABEL, fmtDate, fmtMoney } from "./_shared";

export default function LeadDrawer({ lead, onClose, onChange, onDelete }) {
  const [assignedTo, setAssignedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setAssignedTo(lead?.extra?.assigned_to || "");
      setNotes(lead?.extra?.notes || "");
    }
  }, [lead]);

  if (!lead) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const updated = await patchSubmission(lead.id, patch);
      onChange && onChange(updated);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await deleteSubmission(lead.id);
      onDelete && onDelete(lead.id);
      toast.success("Deleted");
      onClose();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sendRecovery = async () => {
    try {
      await sendRecoveryEmail(lead.id);
      toast.success("Recovery email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const resendConf = async () => {
    try {
      await resendConfirmation(lead.id);
      toast.success("Confirmation queued");
    } catch {
      toast.error("Send failed");
    }
  };

  const extra = lead.extra || {};
  const isAbandoned = lead.form_type === "register" && extra.payment_status !== "paid";
  const isPaid = lead.form_type === "register" && extra.payment_status === "paid";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-slate-100 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Chip tone="lime">{FORM_LABEL[lead.form_type] || lead.form_type}</Chip>
            <div className="mt-2 font-display text-lg font-bold text-slate-900 truncate">{lead.name || "—"}</div>
            <div className="text-xs text-slate-500 truncate">{lead.email}</div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Contact block */}
          <div className="grid gap-2 text-sm">
            <Field icon={Mail} label="Email" value={<a href={`mailto:${lead.email}`} className="text-slate-900 hover:text-lime-600">{lead.email}</a>} />
            {lead.phone && <Field icon={Phone} label="Phone" value={
              <div className="flex items-center gap-2">
                <a href={`tel:${lead.phone}`} className="text-slate-900 hover:text-lime-600">{lead.phone}</a>
                <a href={`https://wa.me/${lead.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="text-[10px] font-semibold uppercase tracking-wider text-lime-700 hover:text-lime-800">WhatsApp</a>
              </div>
            } />}
            {lead.organisation && <Field icon={Building2} label="Organisation" value={lead.organisation} />}
            {lead.interest && <Field icon={User} label="Interest" value={lead.interest} />}
            {lead.pass_type && <Field icon={User} label="Pass" value={lead.pass_type} />}
            {lead.country && <Field icon={User} label="Country" value={lead.country} />}
            {lead.message && <Field icon={MessageSquare} label="Message" value={lead.message} />}
          </div>

          {/* Payment block */}
          {lead.form_type === "register" && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Payment</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {isPaid && <Chip tone="lime">Paid</Chip>}
                {isAbandoned && <Chip tone="amber">Abandoned</Chip>}
                {extra.payment_status === "failed" && <Chip tone="rose">Failed</Chip>}
                <span className="text-sm font-semibold text-slate-900">{fmtMoney(extra.total_amount, extra.currency)}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {extra.delegate_type} • {extra.pricing_window || "—"} • {extra.razorpay_payment_id || extra.razorpay_order_id || ""}
              </div>
            </div>
          )}

          {/* CRM: Assign + Notes */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Team assignment</div>
            <input
              data-testid="drawer-assignee"
              placeholder="Assign to team member (name/email)"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              onBlur={() => assignedTo !== (extra.assigned_to || "") && save({ assigned_to: assignedTo })}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-lime-500"
            />
            <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</div>
            <textarea
              data-testid="drawer-notes"
              rows={3}
              placeholder="Call summary, next steps..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== (extra.notes || "") && save({ notes })}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-lime-500"
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="drawer-toggle-contacted"
                onClick={() => save({ contacted: !extra.contacted })}
                disabled={saving}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  extra.contacted ? "bg-lime-500 text-slate-900 hover:bg-lime-400" : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                {extra.contacted ? "✓ Mark not contacted" : "Mark contacted"}
              </button>
              {extra.contacted_at && <span className="text-[11px] text-slate-500">{fmtDate(extra.contacted_at)}</span>}
            </div>
          </div>

          {/* Meta */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Details</div>
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              <div><span className="text-slate-400">Received:</span> {fmtDate(lead.created_at)}</div>
              <div><span className="text-slate-400">ID:</span> <code className="text-[11px]">{lead.id}</code></div>
              {Object.entries(extra).filter(([k]) => !["assigned_to","notes","contacted","contacted_at","payment_status","razorpay_order_id","razorpay_payment_id","total_amount","currency","delegate_type","base_amount","gst_amount"].includes(k)).map(([k,v]) => v && (
                <div key={k}><span className="text-slate-400">{k}:</span> {String(v)}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-100 p-3 flex flex-wrap gap-2">
          {isAbandoned && (
            <button data-testid="drawer-send-recovery" onClick={sendRecovery} className="flex items-center gap-1.5 rounded-lg bg-lime-500 px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-lime-400">
              <Send className="h-3.5 w-3.5" /> Send recovery email
            </button>
          )}
          {isPaid && (
            <button data-testid="drawer-resend-conf" onClick={resendConf} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">
              <RefreshCcw className="h-3.5 w-3.5" /> Resend confirmation
            </button>
          )}
          <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <Mail className="h-3.5 w-3.5" /> Email
          </a>
          <div className="ml-auto">
            <button data-testid="drawer-delete" onClick={remove} className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</div>
        <div className="text-sm text-slate-800 break-words">{value}</div>
      </div>
    </div>
  );
}
